import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getLitiges, createLitige, updateLitige, deleteLitige, getDossiers, getProspects } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/SearchableSelect';
import { SelectCheckbox } from '../components/BulkActionBar';
import { AlertTriangle, AlertOctagon, CheckCircle2, ChevronRight, Pencil, Plus, Scale, Trash2, X, Paperclip, Upload, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

import {
    uploadLitigeJustificatif,
    deleteLitigeJustificatif,
    getLitigeJustificatifUrl,
    getLitige as fetchLitigeFn,
} from '../lib/api';

const CATEGORIES = [
    { value: 'abandon', label: 'Dossier abandonné', color: '#52525B' },
    { value: 'contentieux', label: 'Contentieux', color: '#DC2626' },
    { value: 'sav', label: 'SAV', color: '#0284C7' },
    { value: 'reserves_non_levees', label: 'Réserves non levées', color: '#EA580C' },
    { value: 'autre', label: 'Autre', color: '#7C3AED' },
];

const PRIORITES = [
    { value: 'basse', label: 'Basse', color: 'bg-green-100 text-green-700' },
    { value: 'normale', label: 'Normale', color: 'bg-blue-100 text-blue-700' },
    { value: 'haute', label: 'Haute', color: 'bg-orange-100 text-orange-700' },
    { value: 'critique', label: 'Critique', color: 'bg-red-100 text-red-700' },
];

const STATUTS = [
    { value: 'ouvert', label: 'Ouvert', color: '#DC2626', icon: AlertOctagon },
    { value: 'en_cours', label: 'En cours', color: '#EA580C', icon: AlertTriangle },
    { value: 'resolu', label: 'Résolu', color: '#16A34A', icon: CheckCircle2 },
    { value: 'abandonne', label: 'Abandonné', color: '#52525B', icon: Scale },
];

export const LitigesPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'manager';
    const [litiges, setLitiges] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('ouvert');
    const [createOpen, setCreateOpen] = useState(false);
    const [editingLitige, setEditingLitige] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [form, setForm] = useState({
        titre: '',
        categorie: 'sav',
        description: '',
        montant_litige: '',
        priorite: 'normale',
        dossier_id: ''
    });

    const load = async () => {
        setLoading(true);
        try {
            const [l, d, p] = await Promise.all([getLitiges(), getDossiers(), getProspects()]);
            setLitiges(l.data || []);
            setDossiers(d.data || []);
            setProspects(p.data || []);
            setSelectedIds([]);
        } catch {
            setLitiges([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Handle deep-link from DossierPage: ?new=1&dossier_id=xxx
    useEffect(() => {
        if (searchParams.get('new') === '1') {
            const dossierId = searchParams.get('dossier_id') || '';
            setForm((p) => ({ ...p, dossier_id: dossierId }));
            setCreateOpen(true);
            // Clear params so reopening doesn't trigger again
            const next = new URLSearchParams(searchParams);
            next.delete('new');
            next.delete('dossier_id');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Reset selection when tab changes
    useEffect(() => { setSelectedIds([]); }, [activeTab]);

    // Build dossier options enriched with client name (for fuzzy search)
    const dossierOptions = dossiers.map((d) => {
        const p = prospects.find((pr) => pr.id === d.prospect_id);
        const clientName = p ? `${p.prenom || ''} ${p.nom || ''}`.trim() : '';
        return {
            value: d.id,
            label: d.titre || 'Sans titre',
            sublabel: clientName ? `Client : ${clientName}` : 'Sans client',
            keywords: `${clientName} ${p?.email || ''} ${p?.telephone || ''} ${d.type_travaux || ''}`,
        };
    });

    const filtered = litiges.filter((l) => l.statut === activeTab);
    const counts = STATUTS.reduce((acc, s) => {
        acc[s.value] = litiges.filter((l) => l.statut === s.value).length;
        return acc;
    }, {});

    const handleCreate = async () => {
        if (!form.titre || !form.description || !form.categorie) {
            toast.error('Titre, catégorie et description requis');
            return;
        }
        if (!form.dossier_id) {
            toast.error('Vous devez rattacher le litige à un dossier');
            return;
        }
        try {
            const payload = { ...form };
            if (payload.montant_litige) payload.montant_litige = Number(payload.montant_litige);
            else delete payload.montant_litige;
            await createLitige(payload);
            toast.success('Litige créé');
            setCreateOpen(false);
            setForm({ titre: '', categorie: 'sav', description: '', montant_litige: '', priorite: 'normale', dossier_id: '' });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur création');
        }
    };

    const handleEdit = async () => {
        if (!editingLitige?.id) return;
        if (!editingLitige.titre || !editingLitige.description) {
            toast.error('Titre et description requis');
            return;
        }
        try {
            const payload = {
                titre: editingLitige.titre,
                categorie: editingLitige.categorie,
                description: editingLitige.description,
                priorite: editingLitige.priorite,
                statut: editingLitige.statut,
                montant_litige: editingLitige.montant_litige ? Number(editingLitige.montant_litige) : null,
            };
            await updateLitige(editingLitige.id, payload);
            toast.success('Litige modifié');
            setEditingLitige(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Supprimer ${selectedIds.length} litige(s) ?`)) return;
        try {
            await Promise.all(selectedIds.map((id) => deleteLitige(id)));
            toast.success(`${selectedIds.length} litige(s) supprimé(s)`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Certaines suppressions ont échoué');
            load();
        }
    };

    const handleBulkStatut = async (statut) => {
        if (!selectedIds.length) return;
        try {
            await Promise.all(selectedIds.map((id) => updateLitige(id, { statut })));
            toast.success(`${selectedIds.length} litige(s) mis à jour`);
            load();
        } catch (e) {
            toast.error('Certaines mises à jour ont échoué');
            load();
        }
    };

    const handleStatutChange = async (id, statut) => {
        try {
            await updateLitige(id, { statut });
            toast.success('Statut mis à jour');
            load();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce litige ?')) return;
        try {
            await deleteLitige(id);
            toast.success('Litige supprimé');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="litiges-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2 sm:gap-3">
                        <Scale className="w-6 h-6 sm:w-7 sm:h-7 text-[#DC2626]" /> Litiges & dossiers morts
                    </h1>
                    <p className="text-sm text-[#52525B] mt-1">{litiges.length} dossier{litiges.length > 1 ? 's' : ''} suivi{litiges.length > 1 ? 's' : ''}</p>
                </div>
                <Button
                    onClick={() => setCreateOpen(true)}
                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-sm w-full sm:w-auto"
                    data-testid="new-litige-btn"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nouveau litige
                </Button>
            </div>

            {/* Bulk action bar (admin/manager only) */}
            {isAdmin && selectedIds.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-[#0A0A0A] text-white px-3 sm:px-4 py-3 rounded-sm gap-2" data-testid="litiges-bulk-bar">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSelectedIds([])}
                            className="hover:bg-white/10 rounded-sm p-1"
                            title="Tout désélectionner"
                            data-testid="litiges-bulk-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-medium">{selectedIds.length} sélectionné(s)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select onValueChange={(v) => handleBulkStatut(v)}>
                            <SelectTrigger className="h-9 flex-1 sm:w-44 rounded-sm bg-white text-[#0A0A0A]" data-testid="litiges-bulk-statut">
                                <SelectValue placeholder="Changer le statut..." />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUTS.map((st) => (
                                    <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="ghost"
                            onClick={handleBulkDelete}
                            className="text-white hover:bg-red-600 hover:text-white shrink-0"
                            data-testid="litiges-bulk-delete"
                        >
                            <Trash2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Supprimer</span>
                        </Button>
                    </div>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                    <TabsList className="bg-[#F4F4F5] p-1 rounded-sm inline-flex flex-nowrap w-max sm:w-auto">
                        {STATUTS.map((s) => {
                            const Icon = s.icon;
                            return (
                                <TabsTrigger
                                    key={s.value}
                                    value={s.value}
                                    className="data-[state=active]:bg-white rounded-sm whitespace-nowrap"
                                    data-testid={`tab-litige-${s.value}`}
                                >
                                    <Icon className="w-4 h-4 mr-2" style={{ color: s.color }} /> {s.label}
                                    {counts[s.value] > 0 && (
                                        <span className="ml-2 bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                            {counts[s.value]}
                                        </span>
                                    )}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </div>

                {STATUTS.map((s) => (
                    <TabsContent key={s.value} value={s.value} className="mt-4">
                        {loading ? (
                            <div className="p-8 text-center"><div className="w-6 h-6 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin mx-auto" /></div>
                        ) : filtered.length === 0 ? (
                            <div className="p-8 text-center text-[#52525B] italic bg-white border border-[#E4E4E7] rounded-sm">Aucun litige avec ce statut</div>
                        ) : (
                            <div className="bg-white border border-[#E4E4E7] rounded-sm divide-y divide-[#E4E4E7]">
                                {isAdmin && (
                                    <div className="px-4 py-2 bg-[#FAFAFA] flex items-center gap-3 text-xs text-[#52525B]">
                                        <SelectCheckbox
                                            checked={filtered.length > 0 && selectedIds.length === filtered.length}
                                            onChange={(checked) => setSelectedIds(checked ? filtered.map((l) => l.id) : [])}
                                            dataTestid="litiges-select-all"
                                        />
                                        <span>Tout sélectionner ({filtered.length})</span>
                                    </div>
                                )}
                                {filtered.map((l) => {
                                    const cat = CATEGORIES.find((c) => c.value === l.categorie) || CATEGORIES[4];
                                    const prio = PRIORITES.find((p) => p.value === l.priorite) || PRIORITES[1];
                                    const isSelected = selectedIds.includes(l.id);
                                    return (
                                        <div key={l.id} className={`p-3 sm:p-4 flex items-start gap-2 sm:gap-3 hover:bg-[#FAFAFA] ${isSelected ? 'bg-[#E85A24]/5' : ''}`} data-testid={`litige-row-${l.id}`}>
                                            {isAdmin && (
                                                <div className="pt-1">
                                                    <SelectCheckbox
                                                        checked={isSelected}
                                                        onChange={(checked) => setSelectedIds((prev) => checked ? [...prev, l.id] : prev.filter((x) => x !== l.id))}
                                                        dataTestid={`litige-select-${l.id}`}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-sm" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                                                            {cat.label}
                                                        </span>
                                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${prio.color}`}>
                                                            {prio.label}
                                                        </span>
                                                        {l.montant_litige > 0 && (
                                                            <span className="text-xs text-[#52525B]">{l.montant_litige.toLocaleString('fr-FR')} €</span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-semibold text-[#0A0A0A]">{l.titre}</h3>
                                                    <p className="text-sm text-[#52525B] line-clamp-2 mt-1">{l.description}</p>
                                                    <p className="text-xs text-[#52525B] mt-2">
                                                        {l.dossier_titre && <span>Dossier : <button onClick={() => navigate(`/dossiers/${l.dossier_id}`)} className="text-[#E85A24] hover:underline">{l.dossier_titre}</button> · </span>}
                                                        {l.prospect_nom && <span>Client : {l.prospect_nom} · </span>}
                                                        Créé le {l.created_at ? format(new Date(l.created_at), 'dd MMM yyyy', { locale: fr }) : ''} par {l.created_by_name}
                                                    </p>
                                                </div>
                                                <div className="flex flex-row sm:flex-col gap-2 shrink-0 items-center sm:items-stretch">
                                                    <Select value={l.statut} onValueChange={(v) => handleStatutChange(l.id, v)}>
                                                        <SelectTrigger className="h-8 rounded-sm flex-1 sm:w-32" data-testid={`litige-statut-${l.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {STATUTS.map((st) => (
                                                                <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => setEditingLitige({ ...l, montant_litige: l.montant_litige ?? '' })}
                                                            className="h-8 w-8 text-[#52525B] hover:text-[#0A0A0A]"
                                                            title="Modifier"
                                                            data-testid={`litige-edit-${l.id}`}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        {isAdmin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDelete(l.id)}
                                                                className="h-8 w-8 text-[#DC2626]"
                                                                title="Supprimer"
                                                                data-testid={`litige-delete-${l.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-lg" data-testid="new-litige-dialog">
                    <DialogHeader>
                        <DialogTitle>Nouveau litige</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="field-label">Titre *</Label>
                            <Input
                                value={form.titre}
                                onChange={(e) => setForm((p) => ({ ...p, titre: e.target.value }))}
                                placeholder="Ex : Réserve sol parquet — Dupont"
                                className="h-10 rounded-sm mt-1"
                                data-testid="litige-titre-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Catégorie *</Label>
                                <Select value={form.categorie} onValueChange={(v) => setForm((p) => ({ ...p, categorie: v }))}>
                                    <SelectTrigger className="h-10 rounded-sm mt-1" data-testid="litige-categorie-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="field-label">Priorité</Label>
                                <Select value={form.priorite} onValueChange={(v) => setForm((p) => ({ ...p, priorite: v }))}>
                                    <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PRIORITES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Dossier lié *</Label>
                            <SearchableSelect
                                options={dossierOptions}
                                value={form.dossier_id}
                                onChange={(v) => setForm((p) => ({ ...p, dossier_id: v }))}
                                placeholder="Rechercher un dossier (titre, client, téléphone...)"
                                emptyMsg="Aucun dossier trouvé"
                                allowClear={false}
                                testId="litige-dossier-select"
                            />
                        </div>
                        <div>
                            <Label className="field-label">Montant en jeu (€) — optionnel</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.montant_litige}
                                onChange={(e) => setForm((p) => ({ ...p, montant_litige: e.target.value }))}
                                placeholder="0.00"
                                className="h-10 rounded-sm mt-1"
                            />
                        </div>
                        <div>
                            <Label className="field-label">Description *</Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                rows={4}
                                className="rounded-sm mt-1"
                                placeholder="Décrivez le litige, le contexte, les éléments factuels..."
                                data-testid="litige-description-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                        <Button onClick={handleCreate} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="litige-create-submit">Créer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!editingLitige} onOpenChange={(o) => !o && setEditingLitige(null)}>
                <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-auto max-h-[90vh] overflow-y-auto" data-testid="edit-litige-dialog">
                    <DialogHeader>
                        <DialogTitle>Modifier le litige</DialogTitle>
                    </DialogHeader>
                    {editingLitige && (
                        <div className="space-y-3">
                            <div>
                                <Label className="field-label">Titre *</Label>
                                <Input
                                    value={editingLitige.titre || ''}
                                    onChange={(e) => setEditingLitige((p) => ({ ...p, titre: e.target.value }))}
                                    className="h-10 rounded-sm mt-1"
                                    data-testid="edit-litige-titre"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="field-label">Catégorie</Label>
                                    <Select value={editingLitige.categorie} onValueChange={(v) => setEditingLitige((p) => ({ ...p, categorie: v }))}>
                                        <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="field-label">Priorité</Label>
                                    <Select value={editingLitige.priorite} onValueChange={(v) => setEditingLitige((p) => ({ ...p, priorite: v }))}>
                                        <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PRIORITES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="field-label">Statut</Label>
                                <Select value={editingLitige.statut} onValueChange={(v) => setEditingLitige((p) => ({ ...p, statut: v }))}>
                                    <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="field-label">Montant en jeu (€)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={editingLitige.montant_litige ?? ''}
                                    onChange={(e) => setEditingLitige((p) => ({ ...p, montant_litige: e.target.value }))}
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Description *</Label>
                                <Textarea
                                    value={editingLitige.description || ''}
                                    onChange={(e) => setEditingLitige((p) => ({ ...p, description: e.target.value }))}
                                    rows={4}
                                    className="rounded-sm mt-1"
                                    data-testid="edit-litige-description"
                                />
                            </div>

                            <JustificatifsSection
                                litige={editingLitige}
                                onChanged={async () => {
                                    try {
                                        const res = await fetchLitigeFn(editingLitige.id);
                                        setEditingLitige((prev) => ({ ...prev, justificatifs: res.data?.justificatifs || [] }));
                                        await load();
                                    } catch {
                                        // silent: list reload is best-effort
                                    }
                                }}
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingLitige(null)}>Annuler</Button>
                        <Button onClick={handleEdit} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white" data-testid="edit-litige-submit">Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};


/**
 * Justificatifs uploader/viewer for a single litige.
 * Supports inline upload, listing, preview (open in new tab) and delete.
 */
const JustificatifsSection = ({ litige, onChanged }) => {
    const [uploading, setUploading] = useState(false);
    const [description, setDescription] = useState('');
    const [preview, setPreview] = useState(null); // currently opened justificatif
    const fileRef = React.useRef(null);
    const justificatifs = litige?.justificatifs || [];

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 10 Mo)');
            return;
        }
        setUploading(true);
        try {
            await uploadLitigeJustificatif(litige.id, file, description.trim());
            toast.success('Justificatif ajouté');
            setDescription('');
            if (fileRef.current) fileRef.current.value = '';
            onChanged && (await onChanged());
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (justifId) => {
        if (!window.confirm('Supprimer ce justificatif ?')) return;
        try {
            await deleteLitigeJustificatif(litige.id, justifId);
            toast.success('Justificatif supprimé');
            onChanged && (await onChanged());
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    return (
        <div className="border-t border-[#E4E4E7] pt-4" data-testid="litige-justificatifs-section">
            <div className="flex items-center justify-between mb-2">
                <Label className="field-label flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Justificatifs ({justificatifs.length})
                </Label>
            </div>
            <p className="text-xs text-[#52525B] mb-3">
                Email du client, PV de réception avec réserves, photos, courriers… (PDF/Image, 10 Mo max)
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <Input
                    placeholder="Description (optionnelle)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="h-9 rounded-sm sm:col-span-2 text-sm"
                    data-testid="justif-description-input"
                />
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="h-9 rounded-sm"
                    data-testid="justif-upload-btn"
                >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? 'Upload...' : 'Joindre un fichier'}
                </Button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="justif-file-input"
                />
            </div>

            {justificatifs.length === 0 ? (
                <p className="text-xs text-[#52525B] italic">Aucun justificatif</p>
            ) : (
                <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-sm">
                    {justificatifs.map((j) => (
                        <li key={j.id} className="flex items-center gap-2 p-2 text-sm" data-testid={`justif-row-${j.id}`}>
                            <Paperclip className="w-4 h-4 text-[#52525B] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{j.filename}</p>
                                {j.description && <p className="text-xs text-[#52525B] truncate">{j.description}</p>}
                                <p className="text-[11px] text-[#A1A1AA]">
                                    {j.uploaded_at ? format(new Date(j.uploaded_at), 'dd MMM yyyy HH:mm', { locale: fr }) : ''}
                                    {j.uploaded_by_name ? ` · ${j.uploaded_by_name}` : ''}
                                    {j.size ? ` · ${(j.size / 1024).toFixed(0)} Ko` : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPreview(j)}
                                className="h-8 w-8 inline-flex items-center justify-center text-[#0284C7] hover:bg-[#0284C7]/10 rounded-sm"
                                title="Voir"
                                data-testid={`justif-view-${j.id}`}
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(j.id)}
                                className="h-8 w-8 text-[#DC2626]"
                                title="Supprimer"
                                data-testid={`justif-delete-${j.id}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            <JustificatifPreviewDialog
                litigeId={litige.id}
                justif={preview}
                onClose={() => setPreview(null)}
            />
        </div>
    );
};


/**
 * Preview dialog for a litige justificatif.
 * - PDF → rendered inside an iframe.
 * - Image → rendered as <img>.
 * - Other content types → link fallback.
 * Includes an "Ouvrir dans un nouvel onglet" button.
 */
const JustificatifPreviewDialog = ({ litigeId, justif, onClose }) => {
    if (!justif) return null;
    const url = getLitigeJustificatifUrl(litigeId, justif.id);
    const ct = (justif.content_type || '').toLowerCase();
    const isPdf = ct.includes('pdf') || (justif.filename || '').toLowerCase().endsWith('.pdf');
    const isImage = ct.startsWith('image/');

    return (
        <Dialog open={!!justif} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-5xl w-[calc(100vw-2rem)] max-h-[92vh] flex flex-col p-0" data-testid="justif-preview-dialog">
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E4E4E7]">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <DialogTitle className="truncate">{justif.filename || 'Justificatif'}</DialogTitle>
                            {justif.description && (
                                <p className="text-xs text-[#52525B] truncate mt-1">{justif.description}</p>
                            )}
                        </div>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] rounded-sm shrink-0"
                            data-testid="justif-open-new-tab"
                        >
                            <Upload className="w-4 h-4 rotate-45" /> Nouvel onglet
                        </a>
                    </div>
                </DialogHeader>
                <div className="flex-1 min-h-0 overflow-auto bg-[#F4F4F5] p-3 flex items-center justify-center">
                    {isImage ? (
                        <img
                            src={url}
                            alt={justif.filename || 'Justificatif'}
                            className="max-w-full max-h-full object-contain"
                            data-testid="justif-preview-img"
                        />
                    ) : isPdf ? (
                        <iframe
                            title={justif.filename || 'Justificatif'}
                            src={url}
                            className="w-full h-full min-h-[70vh] bg-white border-0"
                            data-testid="justif-preview-pdf"
                        />
                    ) : (
                        <div className="text-center py-12 px-6 bg-white border border-[#E4E4E7] rounded-sm">
                            <Paperclip className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
                            <p className="text-sm text-[#0A0A0A] font-medium">Aperçu non supporté pour ce format</p>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#0284C7] hover:underline mt-2 inline-block"
                            >
                                Télécharger le fichier
                            </a>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
